"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { auth, db } from "../firebase"
import { ref, onValue, get, update, push } from "../lib/firebase-database"
import { MobileBottomNav } from "./MobileBottomNav"
import { Calendar, AlertTriangle, ArrowLeft, FileText, XIcon as XMarkIcon, ChevronDownIcon } from "lucide-react"
import { Button } from "./ui/button"
import { Textarea } from "./ui/textarea"
import { InvoiceGenerator } from "./InvoiceGenerator"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog"
import { useAuth } from "../hooks/useAuth"
import Footer from "../components/Footer"
import LoginPopup from "./login-popup"
import LogoutConfirmationPopup from "./LogoutConfirmationPopup"
import AvatarCircle from "./AvatarCircle"

export function UserBookingPage() {
  const [bookings, setBookings] = useState([])
  const [filteredBookings, setFilteredBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [bookingToCancel, setBookingToCancel] = useState(null)
  const [cancellationReason, setCancellationReason] = useState("")
  const [isOwner, setIsOwner] = useState(false)
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false)
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768) // Add state  = useState(null)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isMenuClosing, setIsMenuClosing] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [isBlurred, setIsBlurred] = useState(false)
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()

  // Check if user is owner
  useEffect(() => {
    if (!user) return

    const checkUserRole = async () => {
      const userRef = ref(db, `users/${user.uid}`)
      const snapshot = await get(userRef)
      const userData = snapshot.val()
      setIsOwner(userData?.role === "owner")
    }

    checkUserRole()
  }, [user])

  // Detect screen size for mobile vs desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    handleResize() // Initial check

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Share booking function
  const shareBooking = async (booking) => {
    try {
      // Create a canvas to generate the booking card image
      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")

      // Set canvas dimensions
      canvas.width = 600
      canvas.height = 400

      // Draw background
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw border
      ctx.strokeStyle = "#3b82f6"
      ctx.lineWidth = 8
      ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8)

      // Load and draw villa image
      const img = new Image()
      img.crossOrigin = "anonymous"

      // Wait for image to load
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = booking.villaData?.thumbnail || "/placeholder.svg"
      })

      // Draw image at the top
      ctx.drawImage(img, 10, 10, 580, 180)

      // Draw gradient overlay on image
      const gradient = ctx.createLinearGradient(0, 10, 0, 190)
      gradient.addColorStop(0, "rgba(0,0,0,0.1)")
      gradient.addColorStop(1, "rgba(0,0,0,0.7)")
      ctx.fillStyle = gradient
      ctx.fillRect(10, 10, 580, 180)

      // Draw villa name on image
      ctx.fillStyle = "#ffffff"
      ctx.font = "bold 24px Arial"
      ctx.textAlign = "center"
      ctx.fillText(booking.villaData?.name || "Villa", canvas.width / 2, 160)

      // Draw booking details
      ctx.fillStyle = "#1e3a8a"
      ctx.font = "bold 20px Arial"
      ctx.textAlign = "left"
      ctx.fillText("Booking Details", 20, 220)

      ctx.fillStyle = "#374151"
      ctx.font = "16px Arial"
      ctx.fillText(`Check-in: ${formatDate(booking.checkIn)}`, 20, 250)
      ctx.fillText(`Check-out: ${formatDate(booking.checkOut)}`, 20, 280)
      ctx.fillText(`Guests: ${booking.guests}`, 20, 310)
      ctx.fillText(`Amount: ₹${booking.amount}`, 20, 340)
      ctx.fillText(`Phone: ${booking.phoneNumber || "N/A"}`, 20, 370)

      // Draw booking ID
      ctx.fillStyle = "#1e3a8a"
      ctx.font = "bold 16px Arial"
      ctx.textAlign = "right"
      ctx.fillText(`Booking #${booking.bookingId?.slice(-6) || "000000"}`, 580, 380)

      // Draw VillaKosh logo
      ctx.fillStyle = "#3b82f6"
      ctx.font = "bold 18px Arial"
      ctx.textAlign = "left"
      ctx.fillText("VILLAKOSH", 20, 380)

      // Convert canvas to blob
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"))
      const file = new File([blob], "booking-card.png", { type: "image/png" })

      // Prepare share data
      const shareData = {
        title: "My Booking at " + (booking.villaData?.name || "VillaKosh"),
        text: `Check out my booking at ${booking.villaData?.name || "VillaKosh"}!\nCheck-in: ${formatDate(booking.checkIn)}\nCheck-out: ${formatDate(booking.checkOut)}\nGuests: ${booking.guests}\nPhone: ${booking.phoneNumber || "N/A"}`,
        files: [file],
      }

      // Check if Web Share API supports sharing files
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData)
      } else {
        // Fallback for browsers that don't support file sharing
        // Create a temporary URL for the image
        const imageUrl = URL.createObjectURL(file)

        // Create a custom share modal
        const shareModal = document.createElement("div")
        shareModal.style.position = "fixed"
        shareModal.style.top = "0"
        shareModal.style.left = "0"
        shareModal.style.width = "100%"
        shareModal.style.height = "100%"
        shareModal.style.backgroundColor = "rgba(0,0,0,0.8)"
        shareModal.style.zIndex = "9999"
        shareModal.style.display = "flex"
        shareModal.style.flexDirection = "column"
        shareModal.style.alignItems = "center"
        shareModal.style.justifyContent = "center"

        // Add image preview
        const imagePreview = document.createElement("img")
        imagePreview.src = imageUrl
        imagePreview.style.maxWidth = "90%"
        imagePreview.style.maxHeight = "60%"
        imagePreview.style.borderRadius = "8px"
        imagePreview.style.marginBottom = "20px"
        shareModal.appendChild(imagePreview)

        // Add share options
        const shareOptions = document.createElement("div")
        shareOptions.style.display = "flex"
        shareOptions.style.gap = "20px"
        shareOptions.style.marginBottom = "20px"

        // WhatsApp
        const whatsappBtn = createShareButton("WhatsApp", "#25D366", () => {
          const whatsappText = encodeURIComponent(
            `Check out my booking at ${booking.villaData?.name || "VillaKosh"}!\nCheck-in: ${formatDate(booking.checkIn)}\nCheck-out: ${formatDate(booking.checkOut)}\nPhone: ${booking.phoneNumber || "N/A"}`,
          )
          window.open(`https://wa.me/?text=${whatsappText}`, "_blank")
          document.body.removeChild(shareModal)
          URL.revokeObjectURL(imageUrl)
        })
        shareOptions.appendChild(whatsappBtn)

        // Instagram (opens in app if installed)
        const instagramBtn = createShareButton("Instagram", "#E1306C", () => {
          alert("To share on Instagram: Save the image and upload it to your Instagram app")
          // Provide download link
          const downloadLink = document.createElement("a")
          downloadLink.href = imageUrl
          downloadLink.download = "booking-card.png"
          downloadLink.click()
          document.body.removeChild(shareModal)
          URL.revokeObjectURL(imageUrl)
        })
        shareOptions.appendChild(instagramBtn)

        // Bluetooth (if available)
        if ("bluetooth" in navigator) {
          const bluetoothBtn = createShareButton("Bluetooth", "#0082FC", async () => {
            try {
              const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
              })
              alert(`Selected device: ${device.name}. Bluetooth sharing requires additional implementation.`)
            } catch (error) {
              alert("Bluetooth sharing failed: " + error)
            }
            document.body.removeChild(shareModal)
            URL.revokeObjectURL(imageUrl)
          })
          shareOptions.appendChild(bluetoothBtn)
        }

        // Download option
        const downloadBtn = createShareButton("Download", "#333333", () => {
          const downloadLink = document.createElement("a")
          downloadLink.href = imageUrl
          downloadLink.download = "booking-card.png"
          downloadLink.click()
          document.body.removeChild(shareModal)
          URL.revokeObjectURL(imageUrl)
        })
        shareOptions.appendChild(downloadBtn)

        shareModal.appendChild(shareOptions)

        // Close button
        const closeBtn = document.createElement("button")
        closeBtn.textContent = "Close"
        closeBtn.style.padding = "10px 20px"
        closeBtn.style.backgroundColor = "#f3f4f6"
        closeBtn.style.border = "none"
        closeBtn.style.borderRadius = "8px"
        closeBtn.style.cursor = "pointer"
        closeBtn.onclick = () => {
          document.body.removeChild(shareModal)
          URL.revokeObjectURL(imageUrl)
        }
        shareModal.appendChild(closeBtn)

        document.body.appendChild(shareModal)
      }
    } catch (error) {
      console.error("Error sharing:", error)
      alert("Failed to share booking. Please try again.")
    }
  }

  // Helper function to create share buttons
  const createShareButton = (name, color, onClick) => {
    const button = document.createElement("button")
    button.style.display = "flex"
    button.style.flexDirection = "column"
    button.style.alignItems = "center"
    button.style.backgroundColor = "transparent"
    button.style.border = "none"
    button.style.cursor = "pointer"
    button.style.color = "#ffffff"

    const icon = document.createElement("div")
    icon.style.width = "50px"
    icon.style.height = "50px"
    icon.style.borderRadius = "50%"
    icon.style.backgroundColor = color
    icon.style.display = "flex"
    icon.style.alignItems = "center"
    icon.style.justifyContent = "center"
    icon.style.marginBottom = "5px"
    icon.style.fontSize = "20px"
    icon.textContent = name[0]

    const text = document.createElement("span")
    text.textContent = name
    text.style.fontSize = "12px"

    button.appendChild(icon)
    button.appendChild(text)
    button.onclick = onClick

    return button
  }

  // Handle cancel booking
  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking)
    setShowCancelDialog(true)
  }

  // Generate invoice with complete booking data
  const handleGenerateInvoice = (booking) => {
    const invoiceData = {
      villaName: booking.villaData?.name,
      villaAddress: booking.villaData?.address,
      invoiceNo: booking.bookingId || `INV-${Date.now().toString().slice(-6)}`,
      dateIssued: formatDate(booking.createdAt || Date.now()),
      bookings: [
        {
          bookingId: booking.bookingId,
          guestName: booking.userName || "Guest",
          checkIn: booking.checkIn, // Raw timestamp
          checkInTiming: booking.checkInTiming,
          checkOut: booking.checkOut, // Raw timestamp
          checkOutTiming: booking.checkOutTiming,
          guestCount: booking.guests,
          amount: booking.amount,
          phoneNumber: booking.phoneNumber || "N/A",
        },
      ],
      website: "www.villakosh.com",
      email: "support@villakosh.com",
      phone: "+91 98765 43210",
    }

    setSelectedInvoiceData(invoiceData)
    setShowInvoiceDialog(true)
  }

  // Confirm cancellation request
  const confirmCancelBooking = async () => {
    if (!bookingToCancel || !cancellationReason.trim()) return

    try {
      // Create a cancellation request
      const cancellationRequestRef = ref(db, "cancellationRequests")
      await push(cancellationRequestRef, {
        bookingId: bookingToCancel.id,
        bookingData: bookingToCancel,
        userId: user.uid,
        userName: user.displayName || "User",
        reason: cancellationReason,
        status: "pending",
        requestedAt: Date.now(),
      })

      // Update booking status to cancellation_requested
      await update(ref(db, `bookings/${user.uid}/${bookingToCancel.id}`), {
        cancellationRequested: true,
        cancellationRequestedAt: Date.now(),
      })

      setShowCancelDialog(false)
      setBookingToCancel(null)
      setCancellationReason("")

      // Update local state
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingToCancel.id
            ? { ...booking, cancellationRequested: true, cancellationRequestedAt: Date.now() }
            : booking,
        ),
      )

      alert("Cancellation request submitted successfully. The owner will review your request.")
    } catch (error) {
      console.error("Error submitting cancellation request:", error)
      alert("Failed to submit cancellation request. Please try again.")
    }
  }

  // Format date helper function
  const formatDate = (timestamp, timing) => {
    if (!timestamp) return "N/A"
    const date = new Date(timestamp)
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

    const formattedDate = `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${String(date.getFullYear()).slice(-2)}`
    return timing ? `${formattedDate} (${timing})` : formattedDate
  }

  // Add this helper function near your other utility functions
  const canCancelBooking = (booking) => {
    const today = new Date()
    const checkIn = new Date(booking.checkIn)
    return (
      checkIn > today && // Booking is in the future
      booking.status !== "cancelled" && // Booking is not already cancelled
      !booking.cancellationRequested // No cancellation request pending
    )
  }

  // Get count for each category
  const getCategoryCount = (category) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    switch (category) {
      case "upcoming":
        return bookings.filter((b) => new Date(b.checkIn) > today).length
      case "today":
        return bookings.filter((b) => {
          const checkIn = new Date(b.checkIn)
          return checkIn.toDateString() === today.toDateString()
        }).length
      case "past":
        return bookings.filter((b) => new Date(b.checkOut) < today).length
      case "cancelled":
        return bookings.filter((b) => b.status === "cancelled").length
      default:
        return bookings.length
    }
  }

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    if (showMobileMenu) {
      setIsMenuClosing(true)
      setTimeout(() => {
        setShowMobileMenu(false)
        setIsMenuClosing(false)
        setIsBlurred(false)
        document.body.style.overflow = "auto"
      }, 300)
    } else {
      setShowMobileMenu(true)
      setIsBlurred(true)
      document.body.style.overflow = "hidden"
    }
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true)
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const handleLogoutConfirm = () => {
    auth.signOut()
    setShowLogoutConfirmation(false)
    navigate("/")
  }

  const handleLogoutCancel = () => setShowLogoutConfirmation(false)

  const goToProfile = () => {
    navigate("/profile")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const goToAdminDashboard = () => {
    navigate("/admin")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const toggleLoginPopup = (show) => {
    setShowLoginPopup(show)
    setIsBlurred(show)
    document.body.style.overflow = show ? "hidden" : "auto"
  }

  // Filter bookings based on category
  useEffect(() => {
    if (!bookings) return

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let filtered = [...bookings]

    switch (activeCategory) {
      case "upcoming":
        filtered = filtered.filter((b) => new Date(b.checkIn) > today)
        break
      case "today":
        filtered = filtered.filter((b) => {
          const checkIn = new Date(b.checkIn)
          return checkIn.toDateString() === today.toDateString()
        })
        break
      case "past":
        filtered = filtered.filter((b) => new Date(b.checkOut) < today && b.status !== "cancelled")
        break
      case "cancelled":
        filtered = filtered.filter((b) => b.status === "cancelled")
        break
      default:
        break
    }

    setFilteredBookings(filtered)
  }, [activeCategory, bookings])

  // Fetch user's bookings
  useEffect(() => {
    if (!user) {
      navigate("/login")
      return
    }

    const userBookingsRef = ref(db, `bookings/${user.uid}`)
    const unsubscribe = onValue(userBookingsRef, async (snapshot) => {
      const data = snapshot.val()
      if (!data) {
        setBookings([])
        setFilteredBookings([])
        setLoading(false)
        return
      }

      const bookingsArray = await Promise.all(
        Object.entries(data).map(async ([id, booking]) => {
          const villaRef = ref(db, `villas/${booking.villaId}`)
          const villaSnapshot = await get(villaRef)
          const villaData = villaSnapshot.val()

          return {
            id,
            ...booking,
            villaData: {
              name: villaData?.name || "Villa Name Not Available",
              thumbnail: villaData?.thumbnail || "/placeholder.svg",
              address: villaData?.address || "Address not available",
              ownerPhone: villaData?.whatsappNumber || "Not available",
            },
          }
        }),
      )

      setBookings(bookingsArray)
      setFilteredBookings(bookingsArray)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [user, navigate])

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header for desktop */}
      {!isMobile && (
        <header className="bg-black text-white p-4 animate-fadeIn z-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold">VillaKosh</h1>
              <nav className="flex items-center space-x-4">
                <Link
                  to="/for-owner"
                  className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                >
                  For Owners
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Admin Dashboard
                  </Link>
                )}
                {isOwner && (
                  <Link
                    to="/owner"
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Your Villa
                  </Link>
                )}
                <Link
                  to="/wishlists"
                  className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                >
                  Wishlists
                </Link>
                {user ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowProfileMenu(!showProfileMenu)}
                      className="flex items-center space-x-2 hover:bg-gray-700 px-3 py-2 rounded transition duration-300"
                    >
                      <AvatarCircle
                        src={user.photoURL}
                        name={user.displayName || user.email}
                        className="w-8 h-8"
                        textClassName="text-xs"
                      />
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                    {showProfileMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700 z-50">
                        <div className="px-4 py-2 border-b">
                          <p className="text-sm font-medium">Hi, {user.displayName}</p>
                        </div>
                        <button
                          onClick={goToProfile}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          View Profile
                        </button>
                        <button
                          onClick={() => navigate("/bookings")}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          Your Bookings
                        </button>
                        {isOwner && (
                          <Link
                            to="/owner"
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            onClick={() => setShowProfileMenu(false)}
                          >
                            Owner Dashboard
                          </Link>
                        )}
                        {isAdmin && (
                          <button
                            onClick={goToAdminDashboard}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            Admin Dashboard
                          </button>
                        )}
                        <button
                          onClick={handleLogoutClick}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => toggleLoginPopup(true)}
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Login
                  </button>
                )}
              </nav>
            </div>
          </div>
        </header>
      )}

      {isMobile && showMobileMenu && (
        <div
          className={`fixed inset-0 z-50 ${isMenuClosing ? "mobile-menu-overlay-exit" : "mobile-menu-overlay-enter"}`}
        >
          <div
            className={`bg-white w-64 h-full shadow-lg overflow-y-auto ${isMenuClosing ? "animate-slideOutLeft" : "animate-slideInLeft"}`}
          >
            <div className="p-4 flex bg-black justify-between items-center border-b">
              <h2 className="text-xl text-white font-bold">Menu</h2>
              <XMarkIcon className="h-6 w-6 cursor-pointer text-white" onClick={toggleMobileMenu} />
            </div>
            <nav className="p-4">
              <Link
                to="/for-owner"
                className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                onClick={toggleMobileMenu}
              >
                For House Owners
              </Link>
              <Link
                to="/wishlists"
                className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                onClick={toggleMobileMenu}
              >
                Wishlists
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                  onClick={toggleMobileMenu}
                >
                  Admin Dashboard
                </Link>
              )}
              {user && (
                <button
                  onClick={goToProfile}
                  className="block w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                >
                  Profile
                </button>
              )}
              {user && isOwner && (
                <Link
                  to="/owner"
                  className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                  onClick={toggleMobileMenu}
                >
                  Owner Dashboard
                </Link>
              )}
              {user ? (
                <button
                  onClick={handleLogoutClick}
                  className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => {
                    toggleLoginPopup(true)
                    toggleMobileMenu()
                  }}
                  className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                >
                  Login
                </button>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 bg-[#EDEFEF] z-50 ">
        <div className="flex items-center px-4 py-3 md:container md:mx-auto md:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center text-gray-800">
            <ArrowLeft className="h-6 w-6 mr-2" />
          </button>
          <h1 className="text-xl font-semibold ml-2 md:text-2xl">Your Bookings</h1>
        </div>
      </div>

      <main className="pb-16 px-4 md:container md:mx-auto md:px-8 md:pb-8">
        {/* Category Tabs - Modified for mobile and desktop */}
        {!isOwner && (
          <div className="mb-6 overflow-x-auto -mx-4 md:mx-0 md:flex md:justify-start md:gap-6">
            <div className="flex whitespace-nowrap px-4 md:px-0">
              <button
                onClick={() => setActiveCategory("upcoming")}
                className={`mr-4 pb-2 border-b-2 font-medium text-sm md:text-base md:mr-6 ${
                  activeCategory === "upcoming"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Upcoming ({getCategoryCount("upcoming")})
              </button>
              <button
                onClick={() => setActiveCategory("today")}
                className={`mr-4 pb-2 border-b-2 font-medium text-sm md:text-base md:mr-6 ${
                  activeCategory === "today"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Today ({getCategoryCount("today")})
              </button>
              <button
                onClick={() => setActiveCategory("past")}
                className={`mr-4 pb-2 border-b-2 font-medium text-sm md:text-base md:mr-6 ${
                  activeCategory === "past"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Past ({getCategoryCount("past")})
              </button>
              <button
                onClick={() => setActiveCategory("cancelled")}
                className={`mr-4 pb-2 border-b-2 font-medium text-sm md:text-base md:mr-6 ${
                  activeCategory === "cancelled"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Cancelled ({getCategoryCount("cancelled")})
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : filteredBookings.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {filteredBookings.map((booking) => (
              <div
                key={`${booking.userId}-${booking.id}`}
                className={`bg-white rounded-lg overflow-hidden shadow ${
                  booking.status === "cancelled" ? "opacity-70" : ""
                }`}
              >
                {booking.status === "cancelled" && (
                  <div
                    className={`text-white text-center py-1 font-medium ${
                      booking.cancellationApproved ? "bg-green-500" : "bg-red-500"
                    }`}
                  >
                    {booking.cancellationApproved ? "CANCELLATION ACCEPTED" : "CANCELLED"}
                  </div>
                )}

                {booking.cancellationRequested && !booking.status === "cancelled" && (
                  <div className="bg-yellow-500 text-white text-center py-1 font-medium">CANCELLATION REQUESTED</div>
                )}
                <div className="relative h-40 md:h-48">
                  <img
                    src={booking.villaData?.thumbnail || "/placeholder.svg"}
                    alt={booking.villaData?.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-between">
                    <div className="p-4 text-center">
                      <div className="text-white text-2xl font-bold mb-1">VILLAKOSH</div>
                    </div>
                    <div className="p-4 w-full">
                      <h3 className="text-white text-2xl font-bold text-center md:text-3xl">
                        {booking.villaData?.name}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <div className="flex justify-between mb-3 px-4">
                    <div>
                      <p className="font-semibold text-gray-800 text-lg md:text-xl">{booking.userName || "Guest"}</p>
                      <p className="text-sm text-gray-500 md:text-base">Booked on: {formatDate(booking.createdAt)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleGenerateInvoice(booking)}
                        className="flex items-center justify-center px-3 py-2 bg-green-50 text-green-600 rounded-full hover:bg-green-100 transition-colors text-xs"
                        title="Download Invoice"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        <span>Download Invoice</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-3 px-4">
                    <div>
                      <p className="text-xs text-gray-500 md:text-sm">Check-in</p>
                      <p className="text-sm font-medium md:text-base">
                        {formatDate(booking.checkIn, booking.checkInTiming)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 md:text-sm">Check-out</p>
                      <p className="text-sm font-medium md:text-base">
                        {formatDate(booking.checkOut, booking.checkOutTiming)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 md:text-sm">Guests</p>
                      <p className="text-sm font-medium md:text-base">{booking.guests}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 md:text-sm">Amount</p>
                      <p className="text-sm font-semibold md:text-base">₹{booking.amount}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 md:text-sm">Phone</p>
                      <p className="text-sm font-medium md:text-base">{booking.phoneNumber || "N/A"}</p>
                    </div>
                  </div>

                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      booking.villaData?.address,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mb-4 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors mx-4 rounded"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-blue-600 font-medium text-sm md:text-base">
                          Click here to navigate to villa location
                        </span>
                      </div>
                    </div>
                  </a>

                  <div className="mt-2 pt-3 border-t border-dashed border-gray-300 px-4">
                    <div className="flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <div className="text-sm text-gray-500 md:text-base">Booking #</div>
                          <div className="text-2xl font-mono font-semibold">
                            #{booking.bookingId?.slice(-6) || "000000"}
                          </div>
                        </div>

                        {canCancelBooking(booking) && (
                          <Button
                            onClick={() => handleCancelBooking(booking)}
                            variant="destructive"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1"
                          >
                            Request Cancellation
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg md:text-xl">
              {isOwner && searchQuery
                ? "No bookings found matching your search."
                : "No bookings found in this category."}
            </p>
          </div>
        )}
      </main>

      {/* Footer for desktop */}
      {!isMobile && <Footer />}

      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4 text-red-600">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">Request Cancellation</h3>
            </div>

            <p className="mb-4">
              Please provide a reason for cancelling this booking. The owner will review your request.
            </p>

            {bookingToCancel && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="font-medium">{bookingToCancel.villaData?.name}</p>
                <p className="text-sm text-gray-600">Guest: {bookingToCancel.userName || "Guest"}</p>
                <p className="text-sm text-gray-600">
                  Check-in: {formatDate(bookingToCancel.checkIn, bookingToCancel.checkInTiming)}
                </p>
                <p className="text-sm text-gray-600">
                  Check-out: {formatDate(bookingToCancel.checkOut, bookingToCancel.checkOutTiming)}
                </p>
                <p className="text-sm text-gray-600">Phone: {bookingToCancel.phoneNumber || "N/A"}</p>
                <p className="text-sm text-gray-600">Booking ID: {bookingToCancel.bookingId}</p>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="cancellationReason" className="block text-sm font-medium text-gray-700 mb-1">
                Cancellation Reason
              </label>
              <Textarea
                id="cancellationReason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please explain why you need to cancel this booking..."
                rows={4}
                className="w-full"
                required
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => {
                  setShowCancelDialog(false)
                  setBookingToCancel(null)
                  setCancellationReason("")
                }}
                className="w-1/2 bg-gray-500 hover:bg-gray-600 text-white"
              >
                Keep Booking
              </Button>
              <Button
                type="button"
                onClick={confirmCancelBooking}
                disabled={!cancellationReason.trim()}
                className="w-1/2 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-[95vw] md:max-w-4xl p-0 bg-white">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Booking Invoice</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[80vh]">
            {selectedInvoiceData && <InvoiceGenerator data={selectedInvoiceData} isUserInvoice={true} />}
          </div>
          <DialogFooter className="p-4 border-t">
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conditionally render MobileBottomNav only in mobile view */}
      {isMobile && <MobileBottomNav user={user} activeTab="bookings" />}

      {showLoginPopup && <LoginPopup onClose={() => toggleLoginPopup(false)} />}
      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
      {(isBlurred || showMobileMenu) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"></div>
      )}
    </div>
  )
}

